import http from "./httpService";

/**
 * @typedef {import('../types/api.js').BaseApiResponse} BaseApiResponse
 * @typedef {import('../types/api.js').UserCart} UserCart
 * @typedef {import('../types/api.js').CreateBundleData} CreateBundleData
 * @typedef {import('../types/api.js').DuplicateBundleData} DuplicateBundleData
 * @typedef {import('../types/api.js').UpdatedBundleData} UpdatedBundleData
 * @typedef {import('../types/api.js').CartItem} CartItem
 * @typedef {import('../types/api.js').InsertToBundleRequest} InsertToBundleRequest
 * @typedef {import('../types/api.js').AddSingleActivityRequest} AddSingleActivityRequest
 * @typedef {import('../types/api.js').UpdateActivityRequest} UpdateActivityRequest
 * @typedef {import('../types/api.js').ApiResponseWithError} ApiResponseWithError
 * @typedef {import('../types/api.js').QuoteResponse} QuoteResponse
 */

class UserCartService {
  /**
   * Get user cart data
   * @returns {Promise<BaseApiResponse & { data: UserCart }>} User cart response
   */
  async getUserCart(locale = "US") {
    const response = await http.get("/user-cart", { params: { locale } });
    return response.data;
  }

  /**
   * Create a new cart bundle
   * @returns {Promise<BaseApiResponse & { data: CreateBundleData }>} Create bundle response
   */
  async createBundle(locale = "US") {
    const response = await http.post("/user-cart/bundles", { locale });
    return response.data;
  }

  /**
   * Update bundle name
   * @param {number} bundleId - Bundle ID to update
   * @param {string} newName - New name for the bundle
   * @returns {Promise<ApiResponseWithError & { data: UpdatedBundleData }>} Update bundle name response
   */
  async updateBundleName(bundleId, newName) {
    const response = await http.put(`/user-cart/bundles/${bundleId}`, {
      name: newName,
    });
    return response.data;
  }

  /**
   * Insert item to bundle
   * @param {number} bundleId - Bundle ID
   * @param {InsertToBundleRequest} data - Item data to insert
   * @param {number} locale - Locale code
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Insert to bundle response
   */
  async insertToBundle(bundleId, data) {
    const response = await http.post(
      `/user-cart/bundles/${bundleId}/items`,
      data
    );
    return response.data;
  }

  /**
   * Add single activity to cart
   * @param {AddSingleActivityRequest} data - Activity data to add
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Add single activity response
   */
  async addSingleActivity(data) {
    const response = await http.post("/user-cart/single/items", data);
    return response.data;
  }

  /**
   * Update activity in cart
   * @param {number} itemId - Activity ID to update
   * @param {UpdateActivityRequest} data - Updated activity data
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Update activity response
   */
  async updateActivity(itemId, data) {
    const response = await http.put(`/user-cart/items/${itemId}`, data);
    return response.data;
  }

  /**
   * Remove item from bundle
   * @param {number} itemId - Bundle ID to remove
   * @returns {Promise<BaseApiResponse>} Remove from bundle response
   */
  async removeFromBundle(itemId) {
    const response = await http.delete(`/user-cart/items/${itemId}`);
    return response.data;
  }

  /**
   * Remove bundle from cart
   * @param {number} bundleId - Bundle ID to remove
   * @returns {Promise<BaseApiResponse>} Remove from bundle response
   */
  async removeBundle(bundleId) {
    const response = await http.delete(`/user-cart/bundles/${bundleId}`);
    return response.data;
  }

  /**
   * Duplicate a bundle in the cart
   * @param {number} bundleId - Bundle ID to duplicate
   * @returns {Promise<ApiResponseWithError & { data: DuplicateBundleData }>} Duplicate bundle response
   */
  async duplicateBundle(bundleId) {
    const response = await http.post(`/user-cart/duplicate/${bundleId}`);
    return response.data;
  }

  /**
   * Convert single cart items to a bundle
   * @param {number} cartId - Cart ID
   * @returns {Promise<ApiResponseWithError & { data: DuplicateBundleData }>} Convert single to bundle response
   */
  async convertSingleToBundle(cartId) {
    const response = await http.put(`/user-cart/convert-single-to-bundle`, {
      cart_id: cartId,
    });
    return response.data;
  }

  /**
   * Convert bundle item to single cart item
   * @param {number} itemId - Item ID to convert
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Convert bundle item to single response
   */
  async convertBundleItemToSingle(itemId) {
    const response = await http.put(
      `/user-cart/convert-item-to-single/${itemId}`
    );
    return response.data;
  }

  /**
   * Convert single item to bundle item
   * @param {number} itemId - Item ID to convert
   * @param {Object} [data] - Optional data with bundle_id
   * @param {number} [data.bundle_id] - Optional bundle ID to add the item to
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Convert single item to bundle response
   */
  async convertSingleItemToBundle(itemId, data) {
    const response = await http.put(
      `/user-cart/convert-item-to-bundle/${itemId}`,
      data
    );
    return response.data;
  }

  /**
   * Move item/activity between bundles or to single cart
   * @param {number} itemId - Item ID to move
   * @param {number|null} [bundleId] - Optional bundle ID to move the item to (null for single cart)
   * @returns {Promise<ApiResponseWithError & { data: CartItem }>} Move item response
   */
  async moveItemBetweenBundle(itemId, bundleId = null) {
    const response = await http.put(`/user-cart/change-item-bundle/${itemId}`, {
      bundle_id: bundleId,
    });
    return response.data;
  }

  /**
   * Download a quote PDF for a specific cart
   * @param {number} cartId - Cart ID to download quote for
   * @param {Object} data - Quote data including email, organization info, and address
   * @returns {Promise<void>} Triggers file download in browser
   */
  async downloadQuote(cartId, data) {
    const response = await http.post(
      `/user-cart-quotes/download/${cartId}`,
      data,
      { responseType: "blob" }
    );

    // Try to extract filename from Content-Disposition header
    let filename = `quote-${cartId}.pdf`; // fallback default

    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"];

    console.log("All headers:", response.headers);
    console.log("Content-Disposition:", contentDisposition);

    if (contentDisposition) {
      // Try multiple patterns to match different Content-Disposition formats
      const patterns = [
        /filename\*?=['"]?([^'";\r\n]+)['"]?/i, // filename="file.pdf" or filename*=UTF-8''file.pdf
        /filename=([^;\r\n]+)/i, // filename=file.pdf
        /attachment;\s*filename\*?=['"]?([^'";\r\n]+)['"]?/i, // attachment; filename="file.pdf"
        /attachment;\s*filename=([^;\r\n]+)/i, // attachment; filename=file.pdf
      ];

      for (const pattern of patterns) {
        const match = contentDisposition.match(pattern);
        if (match && match[1]) {
          filename = match[1].trim();
          // Remove quotes if present
          filename = filename.replace(/^["']|["']$/g, "");
          break;
        }
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Request a quote to be sent via email
   * @param {string} email - Email address to send the quote to
   * @returns {Promise<BaseApiResponse>} Request quote response
   */
  async requestAQuote(data) {
    const response = await http.post("/user-cart-quotes/request", data);
    return response.data;
  }

  /**
   * Re-request a quote for a specific cart to be sent via email
   * @param {number} cartId - Cart ID to re-request quote for
   * @param {string} email - Email address to send the quote to
   * @returns {Promise<BaseApiResponse>} Re-request quote response
   */
  async reRequestAQuote(cartId, email) {
    const response = await http.post(`/user-cart-quotes/re-request/${cartId}`, {
      email,
    });
    return response.data;
  }

  /**
   * Update the quantity of a bundle
   * @param {number} bundleId - Bundle ID to update quantity for
   * @param {number} quantity - New quantity for the bundle
   * @returns {Promise<BaseApiResponse>} Update bundle quantity response
   */
  async updateBundleQuantity(bundleId, quantity) {
    const response = await http.put(
      `/user-cart/update-bundle-quantity/${bundleId}`,
      {
        quantity,
      }
    );
    return response.data;
  }
}

export default new UserCartService();
